import React, { useContext, useEffect, useState } from 'react'

import Button from 'react-bootstrap/Button'

import LoadingContext from '../../context/Loading/Loading'
import UserContext from '../../context/User/User'
import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'
import { b64toBlob, checkPermission } from '../../utilities/helpers'
import AuthService from '../../utilities/services/auth.service'
import {
  ticketExchangeGetEvents,
  listFundraisingCampaigns,
  getFundraisingCampaign,
  publishFundraisingCampaign as publishFundraisingCampaignApi,
  saveFundraisingCampaign as saveFundraisingCampaignApi,
  upload,
} from '../../utilities/api'
import { NoPermissionsContainer } from '../NoPermissionsContainer'
import { PageLoadingContainer } from '../PageLoadingContainer'
import FundraisersList from './FundraisersList'
import FundraiserCampaignWizard from './FundraiserCampaignWizard'
import './fundraisersWrapper.scss'
import {
  createInitialFormData,
  hydrateCampaignFormData,
} from './fundraisersFormDefaults'

const steps = [
  {
    title: 'Campaign basics',
    shortLabel: 'Campaign basics',
    description: 'Core campaign setup and goals.',
  },
  {
    title: 'Connect to ticketing',
    shortLabel: 'Connect to ticketing',
    description: 'Link this campaign to event checkout.',
  },
  {
    title: 'Participants',
    shortLabel: 'Participants',
    description: 'Configure players, teams, or groups.',
  },
  {
    title: 'Corporate sponsors',
    shortLabel: 'Corporate sponsors',
    description: 'Add matching or pledged sponsor support.',
  },
  {
    title: 'Public page design',
    shortLabel: 'Public page design',
    description: 'Customize page URL, content, and branding.',
  },
  {
    title: 'Checkout settings',
    shortLabel: 'Checkout settings',
    description: 'Control donation prompts in checkout.',
  },
  {
    title: 'Review & publish',
    shortLabel: 'Review & publish',
    description: 'Confirm your campaign settings before going live.',
  },
]

const initialParticipantForm = { name: '', email: '', goal: '' }

const validateStep = (stepNumber, formData) => {
  const errors = {}

  if (stepNumber === 1) {
    if (!formData.campaignName?.trim())
      errors.campaignName = 'Campaign name is required.'
    if (!formData.campaignType)
      errors.campaignType = 'Campaign type is required.'
    if (
      formData.campaignType !== 'Donation Campaign' &&
      (!formData.goalAmount || Number(formData.goalAmount) <= 0)
    ) {
      errors.goalAmount = 'Goal amount must be greater than zero.'
    }
  }

  if (
    stepNumber === 2 &&
    formData.connectionType === 'Specific Event' &&
    !formData.eventUuid
  ) {
    errors.eventUuid = 'Select an event.'
  }

  if (stepNumber === 3 && formData.enableParticipantFundraising === 'Yes') {
    if (formData.participants.length === 0) {
      errors.participants =
        'Add at least one participant or disable participant fundraising.'
    }
  }

  if (stepNumber === 4 && formData.addCorporateSponsors === 'Yes') {
    if (!formData.companyName?.trim())
      errors.companyName = 'Company name is required.'
    if (!formData.sponsorshipType)
      errors.sponsorshipType = 'Sponsorship type is required.'
    if (
      formData.sponsorshipType === 'Matching Donation' &&
      !formData.maximumMatch?.trim()
    ) {
      errors.maximumMatch = 'Maximum match is required.'
    }
    if (
      formData.sponsorshipType === 'Flat Donation' &&
      !formData.donationAmount?.trim()
    ) {
      errors.donationAmount = 'Donation amount is required.'
    }
    if (
      formData.sponsorshipType === 'Per-Ticket Pledge' &&
      !formData.amountPerTicket?.trim()
    ) {
      errors.amountPerTicket = 'Amount per ticket is required.'
    }
  }

  if (stepNumber === 5 && !formData.pageUrlSlug?.trim()) {
    errors.pageUrlSlug = 'Page URL slug is required.'
  }

  if (stepNumber === 6 && formData.showDonationInCheckout === 'Yes') {
    if (!formData.donationTitle?.trim())
      errors.donationTitle = 'Donation title is required.'
    if (!formData.donationDescription?.trim()) {
      errors.donationDescription = 'Donation description is required.'
    }
    if (formData.suggestedAmounts.length === 0) {
      errors.suggestedAmounts = 'Select at least one suggested amount.'
    }
  }

  return errors
}

const validateAllSteps = (formData) => {
  const errorsByStep = {}
  for (let stepNumber = 1; stepNumber <= steps.length; stepNumber += 1) {
    const stepErrors = validateStep(stepNumber, formData)
    if (Object.keys(stepErrors).length > 0) {
      errorsByStep[stepNumber] = stepErrors
    }
  }
  return errorsByStep
}

export default function FundraisersWrapper() {
  const { hideLoading } = useContext(LoadingContext)
  const { orgPermissions } = useContext(UserContext)
  const orgPermissionsReady = Array.isArray(orgPermissions)
  const currentUser = AuthService.getUser()?.user
  const activeOrganization = AuthService.getOrg()?.[0]
  const isSuperAdmin = Boolean(
    currentUser?.superAdmin ||
    activeOrganization?.members?.some(
      (member) => member?.uuid === currentUser?.uuid && member?.superAdmin
    )
  )
  const hasPermission =
    isSuperAdmin ||
    (orgPermissionsReady &&
      checkPermission(
        AuthService.getPermissions(),
        ORGANIZATION_PERMISSION_SLUGS.MANAGE_FUNDRAISING
      ))

  const [view, setView] = useState('list')
  const [campaigns, setCampaigns] = useState([])
  const [isFetchingCampaigns, setIsFetchingCampaigns] = useState(true)
  const [isLoadingCampaign, setIsLoadingCampaign] = useState(false)
  const [currentStep, setCurrentStep] = useState(1)
  const [campaignUuid, setCampaignUuid] = useState('')
  const [events, setEvents] = useState([])
  const [isFetchingEvents, setIsFetchingEvents] = useState(true)
  const [participantForm, setParticipantForm] = useState(initialParticipantForm)
  const [errors, setErrors] = useState({})
  const [submitState, setSubmitState] = useState({
    isSavingDraft: false,
    isPublishing: false,
    status: '',
    message: '',
  })
  const [formData, setFormData] = useState(createInitialFormData())
  const [bannerImage, setBannerImage] = useState('')
  const [initialBannerImage, setInitialBannerImage] = useState('')
  const [bannerImageId, setBannerImageId] = useState(null)
  const [sponsorLogo, setSponsorLogo] = useState('')
  const [initialSponsorLogo, setInitialSponsorLogo] = useState('')
  const [sponsorLogoId, setSponsorLogoId] = useState(null)

  const activeOrganizationUuid = AuthService.getOrg()?.[0]?.uuid || ''

  const loadCampaigns = async () => {
    if (!activeOrganizationUuid) {
      setCampaigns([])
      return
    }

    setIsFetchingCampaigns(true)
    try {
      const response = await listFundraisingCampaigns(activeOrganizationUuid)
      setCampaigns(
        Array.isArray(response?.data?.campaigns) ? response.data.campaigns : []
      )
    } catch (error) {
      console.error('Failed to load fundraising campaigns:', error)
      setCampaigns([])
    } finally {
      setIsFetchingCampaigns(false)
      hideLoading()
    }
  }

  useEffect(() => {
    if (!orgPermissionsReady && !isSuperAdmin) return
    if (!hasPermission) {
      hideLoading()
      setIsFetchingCampaigns(false)
      return
    }

    if (view !== 'list') return

    loadCampaigns()
  }, [
    orgPermissionsReady,
    hasPermission,
    hideLoading,
    activeOrganizationUuid,
    view,
  ])

  const openCreateWizard = () => {
    setCampaignUuid('')
    setCurrentStep(1)
    setErrors({})
    setSubmitState({
      isSavingDraft: false,
      isPublishing: false,
      status: '',
      message: '',
    })
    setFormData(createInitialFormData())
    setBannerImage('')
    setInitialBannerImage('')
    setBannerImageId(null)
    setSponsorLogo('')
    setInitialSponsorLogo('')
    setSponsorLogoId(null)
    setView('wizard')
  }

  const openEditCampaign = async (selectedCampaignUuid) => {
    if (!activeOrganizationUuid || !selectedCampaignUuid) return

    setIsLoadingCampaign(true)
    setView('wizard')
    setCurrentStep(1)
    setErrors({})
    setSubmitState({
      isSavingDraft: false,
      isPublishing: false,
      status: '',
      message: '',
    })

    try {
      const response = await getFundraisingCampaign(
        activeOrganizationUuid,
        selectedCampaignUuid
      )
      const campaign = response?.data?.campaign
      if (!campaign) throw new Error('Campaign not found')

      setCampaignUuid(campaign.uuid)
      const hydrated = hydrateCampaignFormData(campaign)
      setFormData(hydrated)
      setBannerImage(hydrated.bannerImageUrl || '')
      setInitialBannerImage(hydrated.bannerImageUrl || '')
      setBannerImageId(hydrated.bannerImageId || null)
      setSponsorLogo(hydrated.sponsorLogoUrl || '')
      setInitialSponsorLogo(hydrated.sponsorLogoUrl || '')
      setSponsorLogoId(hydrated.sponsorLogoId || null)
    } catch (error) {
      console.error('Failed to load fundraising campaign:', error)
      setSubmitState({
        isSavingDraft: false,
        isPublishing: false,
        status: 'danger',
        message: 'Unable to load campaign. Returning to list.',
      })
      setView('list')
    } finally {
      setIsLoadingCampaign(false)
    }
  }

  const backToList = async () => {
    setView('list')
    setCurrentStep(1)
    setCampaignUuid('')
    setErrors({})
    setSubmitState({
      isSavingDraft: false,
      isPublishing: false,
      status: '',
      message: '',
    })
    setFormData(createInitialFormData())
    setBannerImage('')
    setInitialBannerImage('')
    setBannerImageId(null)
    setSponsorLogo('')
    setInitialSponsorLogo('')
    setSponsorLogoId(null)
    await loadCampaigns()
  }

  useEffect(() => {
    if (!orgPermissionsReady && !isSuperAdmin) return
    if (!hasPermission) {
      hideLoading()
      setIsFetchingEvents(false)
      return
    }
    if (view !== 'wizard') return

    if (!activeOrganizationUuid) {
      hideLoading()
      setIsFetchingEvents(false)
      setEvents([])
      return
    }

    const loadEvents = async () => {
      setIsFetchingEvents(true)
      try {
        const eventsResponse = await ticketExchangeGetEvents(
          activeOrganizationUuid
        )
        const eventsData = Array.isArray(eventsResponse?.data)
          ? eventsResponse.data
          : []
        setEvents(eventsData)
        if (eventsData.length > 0) {
          setFormData((prevState) => {
            const hasSelectedEvent = eventsData.some(
              (event) => event?.uuid === prevState.eventUuid
            )
            if (hasSelectedEvent) return prevState

            return {
              ...prevState,
              eventUuid: eventsData[0]?.uuid || '',
            }
          })
        } else {
          setFormData((prevState) =>
            prevState.eventUuid ? { ...prevState, eventUuid: '' } : prevState
          )
        }
      } catch (error) {
        console.error('Failed to load events for fundraiser wizard:', error)
        setEvents([])
      } finally {
        setIsFetchingEvents(false)
      }
    }

    loadEvents()
  }, [
    orgPermissionsReady,
    hasPermission,
    hideLoading,
    activeOrganizationUuid,
    view,
  ])

  const updateField = (fieldName, value) => {
    setFormData((prevState) => ({ ...prevState, [fieldName]: value }))
  }

  const updateConnectionType = (connectionType) => {
    setFormData((prevState) => ({
      ...prevState,
      connectionType,
      applyFundraiserTo:
        connectionType === 'Specific Event'
          ? 'All tickets'
          : prevState.applyFundraiserTo,
      ...(connectionType !== 'Specific Event' ? { eventUuid: '' } : {}),
      selectedSeasonUuids: [],
      selectedPackageUuids: [],
      donationRules: [],
    }))
  }

  const toggleArrayValue = (fieldName, value) => {
    const currentValues = Array.isArray(formData[fieldName])
      ? formData[fieldName]
      : []
    const nextValues = currentValues.includes(value)
      ? currentValues.filter((item) => item !== value)
      : [...currentValues, value]
    updateField(fieldName, nextValues)
  }

  const addParticipant = () => {
    if (!participantForm.name.trim()) return
    const nextParticipant = {
      name: participantForm.name.trim(),
      email: participantForm.email.trim(),
      goal: participantForm.goal.trim(),
    }
    updateField('participants', [...formData.participants, nextParticipant])
    setParticipantForm(initialParticipantForm)
  }

  const removeParticipant = (index) => {
    updateField(
      'participants',
      formData.participants.filter(
        (_, participantIndex) => participantIndex !== index
      )
    )
  }

  const nextStep = () =>
    setCurrentStep((prevState) => Math.min(prevState + 1, steps.length))
  const previousStep = () =>
    setCurrentStep((prevState) => Math.max(prevState - 1, 1))

  const handleNextStep = () => {
    const stepErrors = validateStep(currentStep, formData)
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors)
      return
    }
    setErrors({})
    nextStep()
  }

  const handleStepSelect = (stepNumber) => {
    if (stepNumber <= currentStep) {
      setCurrentStep(stepNumber)
      setErrors({})
      return
    }

    if (stepNumber === currentStep + 1) {
      handleNextStep()
    }
  }

  const uploadImageIfNeeded = async (image) => {
    if (!image || image.startsWith('https://')) return null
    const block = image.split(';')
    const contentType = block[0].split(':')[1]
    const realData = block[1].split(',')[1]
    const formDataUpload = new FormData()
    formDataUpload.append('files', b64toBlob(realData, contentType))
    const res = await upload(formDataUpload)
    return res?.data?.[0]?.id ?? null
  }

  const resolveImageId = async (image, initialImage, existingId) => {
    if (!image) {
      return initialImage ? null : undefined
    }
    if (image === initialImage) {
      return existingId || undefined
    }
    return uploadImageIfNeeded(image)
  }

  const getSubmitPayload = async () => {
    const resolvedBannerImageId = await resolveImageId(
      bannerImage,
      initialBannerImage,
      bannerImageId
    )
    const resolvedSponsorLogoId =
      formData.addCorporateSponsors === 'Yes'
        ? await resolveImageId(sponsorLogo, initialSponsorLogo, sponsorLogoId)
        : undefined

    return {
      organizationUUID: activeOrganizationUuid,
      campaignUuid,
      formData,
      ...(resolvedBannerImageId !== undefined
        ? { bannerImageId: resolvedBannerImageId }
        : {}),
      ...(resolvedSponsorLogoId !== undefined
        ? { sponsorLogoId: resolvedSponsorLogoId }
        : {}),
    }
  }

  const saveDraft = async () => {
    setSubmitState({
      isSavingDraft: true,
      isPublishing: false,
      status: '',
      message: '',
    })
    if (!activeOrganizationUuid) {
      setSubmitState({
        isSavingDraft: false,
        isPublishing: false,
        status: 'danger',
        message: 'No active organization selected. Refresh and try again.',
      })
      return
    }
    try {
      const response = await saveFundraisingCampaignApi(
        await getSubmitPayload()
      )
      if (response?.data?.campaignUuid) {
        setCampaignUuid(response.data.campaignUuid)
      }
      setSubmitState({
        isSavingDraft: false,
        isPublishing: false,
        status: 'success',
        message: response?.data?.message || 'Draft saved successfully.',
      })
    } catch (error) {
      setSubmitState({
        isSavingDraft: false,
        isPublishing: false,
        status: 'danger',
        message: 'Unable to save draft right now.',
      })
    }
  }

  const publishCampaign = async () => {
    const errorsByStep = validateAllSteps(formData)
    if (Object.keys(errorsByStep).length > 0) {
      const firstInvalidStep = Number(Object.keys(errorsByStep)[0])
      setCurrentStep(firstInvalidStep)
      setErrors(errorsByStep[firstInvalidStep])
      setSubmitState({
        isSavingDraft: false,
        isPublishing: false,
        status: 'danger',
        message: 'Fix required fields before publishing.',
      })
      return
    }

    setErrors({})
    setSubmitState({
      isSavingDraft: false,
      isPublishing: true,
      status: '',
      message: '',
    })
    if (!activeOrganizationUuid) {
      setSubmitState({
        isSavingDraft: false,
        isPublishing: false,
        status: 'danger',
        message: 'No active organization selected. Refresh and try again.',
      })
      return
    }

    try {
      const response = await publishFundraisingCampaignApi(
        await getSubmitPayload()
      )
      if (response?.data?.campaignUuid) {
        setCampaignUuid(response.data.campaignUuid)
      }
      setSubmitState({
        isSavingDraft: false,
        isPublishing: false,
        status: 'success',
        message: response?.data?.message || 'Campaign published successfully.',
      })
    } catch (error) {
      setSubmitState({
        isSavingDraft: false,
        isPublishing: false,
        status: 'danger',
        message: 'Unable to publish campaign right now.',
      })
    }
  }

  const connectedEventName =
    events.find((event) => event?.uuid === formData.eventUuid)?.name ||
    'Not selected'

  if (!orgPermissionsReady && !isSuperAdmin) {
    return <PageLoadingContainer style="without-sidebar" />
  }

  if (!hasPermission) {
    return (
      <div className="spacer-sm">
        <NoPermissionsContainer />
      </div>
    )
  }

  if (view === 'list') {
    return (
      <FundraisersList
        campaigns={campaigns}
        isLoading={isFetchingCampaigns}
        organizationName={activeOrganization?.name || ''}
        onCreate={openCreateWizard}
        onEdit={openEditCampaign}
      />
    )
  }

  if (isLoadingCampaign || isFetchingEvents) {
    return <PageLoadingContainer style="without-sidebar" />
  }

  return (
    <section className="spacer-sm">
      <header className="section-heading--flex section-header-sm">
        <div className="section-heading">
          <h1>Fundraising campaigns</h1>
          <p className="section-header-desc">
            Set up donation campaigns, sponsors, and checkout prompts.
          </p>
        </div>
        <Button variant="outline-light" onClick={backToList}>
          Back to campaigns
        </Button>
      </header>

      <FundraiserCampaignWizard
        campaignUuid={campaignUuid}
        currentStep={currentStep}
        steps={steps}
        formData={formData}
        errors={errors}
        events={events}
        participantForm={participantForm}
        bannerImage={bannerImage}
        sponsorLogo={sponsorLogo}
        submitState={submitState}
        connectedEventName={connectedEventName}
        organizationName={activeOrganization?.name || ''}
        onStepSelect={handleStepSelect}
        onFieldChange={updateField}
        onConnectionTypeChange={updateConnectionType}
        onToggleArrayValue={toggleArrayValue}
        onParticipantFormChange={setParticipantForm}
        onAddParticipant={addParticipant}
        onRemoveParticipant={removeParticipant}
        onBannerImageChange={setBannerImage}
        onSponsorLogoChange={setSponsorLogo}
        onBack={previousStep}
        onNext={handleNextStep}
        onSaveDraft={saveDraft}
        onPublish={publishCampaign}
      />
    </section>
  )
}
